// Ch78 · the Leads area's move-in timeline — pure model, no React.
//
// Grant (2026-09-07): "Leads tab we can explore the data about them — like
// the confidence scores — maybe a timeline view, interactive, where we can see
// all the move-in dates, VISUALIZING OUR DATA."
//
// Two axes that already exist on every lead row, nothing invented:
//   x  = the move-in date the renter typed (leads.move_in_date). Free text
//        ("August", "asap") is NOT guessed into a date — it lands in the
//        undated list with its raw words, per the estate's UNKNOWN doctrine.
//   y  = the scorer-v2 intel tier lane (ULTRA_REAL … ULTRA_FAKE, or unscored)
//        — GR-16's business-quality dimension, read from verification_flags.
//   size = stated budget; not stated -> the smallest dot, never zero-as-value.

import type { Theme } from "@mui/material/styles";
import type { Lead } from "../components/clients/types";
import type { DashboardLeadRow } from "./dashboardLeads";
import { leadIntelTier, type LeadIntelTier } from "../components/clients/leadQuality";

/** The Website form's SMS tick (leads.sms_opted_in). Read structurally so
 * this file does not depend on the field reaching the Lead type (PR #487
 * adds it and a shared helper; this stays correct either way). */
function leadTextsOk(lead: Lead): boolean {
  return (lead as { sms_opted_in?: boolean | null }).sms_opted_in === true;
}

export type TimelineTone = "real" | "uncertain" | "fake" | "unscored";

/**
 * The one place a tone becomes a colour.
 *
 * It lived in components/clients/LeadsTimeline.tsx, a recharts scatter chart
 * that NOTHING rendered any more — so every file wanting this ten-line switch
 * (the work list, the claim inbox, the move-in calendar) imported recharts with
 * it. A dead component is not free when a live helper is hiding inside it.
 *
 * Takes the Theme rather than reading one, so this file stays pure: no React,
 * no hooks, no chart library.
 */
export function toneColor(theme: Theme, tone: TimelineTone): string {
  switch (tone) {
    case "real":
      return theme.palette.success.main;
    case "uncertain":
      return theme.palette.warning.main;
    case "fake":
      return theme.palette.error.main;
    default:
      return theme.palette.text.disabled;
  }
}

/** Lane order, top to bottom. Index is the y value on the chart. */
export const TIMELINE_LANES: ReadonlyArray<{ key: LeadIntelTier | "UNSCORED"; label: string; tone: TimelineTone }> = [
  { key: "ULTRA_REAL", label: "Ultra real", tone: "real" },
  { key: "REAL", label: "Real", tone: "real" },
  { key: "UNCERTAIN", label: "Uncertain", tone: "uncertain" },
  { key: "LIKELY_FAKE", label: "Likely fake", tone: "fake" },
  { key: "ULTRA_FAKE", label: "Ultra fake", tone: "fake" },
  { key: "UNSCORED", label: "Not scored", tone: "unscored" },
];

export function tierTone(tier: LeadIntelTier | null): TimelineTone {
  if (tier === "ULTRA_REAL" || tier === "REAL") return "real";
  if (tier === "UNCERTAIN") return "uncertain";
  if (tier === "LIKELY_FAKE" || tier === "ULTRA_FAKE") return "fake";
  return "unscored";
}

export function laneIndex(tier: LeadIntelTier | null): number {
  const key = tier ?? "UNSCORED";
  const idx = TIMELINE_LANES.findIndex((l) => l.key === key);
  return idx === -1 ? TIMELINE_LANES.length - 1 : idx;
}

const DAY_MS = 86_400_000;

/**
 * The move-in date as a local-midnight Date, or null when the text is not a
 * date. Accepts ISO `YYYY-MM-DD` (optionally with a time trailer, which is
 * dropped — the renter chose a day, not an instant) and US `M/D/YYYY`.
 * Anything else — "August", "next month", "asap" — is honestly undated.
 */
export function parseLeadMoveInDate(raw: string | null | undefined): Date | null {
  if (!raw || typeof raw !== "string") return null;
  const text = raw.trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const us = iso ? null : text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  let y: number, m: number, d: number;
  if (iso) {
    y = Number(iso[1]); m = Number(iso[2]) - 1; d = Number(iso[3]);
  } else if (us) {
    m = Number(us[1]) - 1; d = Number(us[2]); y = Number(us[3]);
  } else {
    return null;
  }
  const date = new Date(y, m, d);
  // Reject impossible days the constructor would silently roll over (Feb 30).
  if (date.getFullYear() !== y || date.getMonth() !== m || date.getDate() !== d) return null;
  return date;
}

/** Stated budget as a number, or null. Never zero for "not stated". */
export function leadBudgetNumber(lead: Pick<Lead, "budget" | "budget_number">): number | null {
  if (typeof lead.budget_number === "number" && Number.isFinite(lead.budget_number) && lead.budget_number > 0) {
    return lead.budget_number;
  }
  const digits = (lead.budget || "").replace(/[^0-9]/g, "");
  if (!digits) return null;
  const n = Number(digits);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export interface LeadTimelinePoint {
  id: string;
  name: string;
  /** Local-midnight ms of the move-in date — the x value. */
  t: number;
  /** Lane index — the y value (see TIMELINE_LANES). */
  lane: number;
  tier: LeadIntelTier | null;
  tone: TimelineTone;
  budget: number | null;
  /** Dot size — the z value. Budget-less leads get the floor, not zero. */
  z: number;
  textsOk: boolean;
  converted: boolean;
  moveInLabel: string;
  lead: Lead;
}

export interface LeadTimelineModel {
  points: LeadTimelinePoint[];
  /** Leads whose move-in text is not a date (or absent), in input order. */
  undated: Lead[];
  /** Points whose move-in day is before today. */
  pastCount: number;
  /** Points moving in within the next 30 days (today inclusive). */
  next30Count: number;
  /** [min, max] ms for the x-axis: at least a week back and 60 days ahead. */
  domain: [number, number];
  todayMs: number;
}

export const BUDGET_Z_FLOOR = 60;
export const BUDGET_Z_CEIL = 420;

function budgetZ(budget: number | null): number {
  if (budget === null) return BUDGET_Z_FLOOR;
  // $1k → floor-ish, $5k+ → ceiling. Linear in between; monotone by construction.
  const clamped = Math.max(1000, Math.min(5000, budget));
  return BUDGET_Z_FLOOR + ((clamped - 1000) / 4000) * (BUDGET_Z_CEIL - BUDGET_Z_FLOOR);
}

function localMidnight(now: Date): number {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
}

const MOVE_IN_FMT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });

export function formatTimelineDay(ms: number): string {
  return MOVE_IN_FMT.format(new Date(ms));
}

export function buildLeadTimeline(leads: Lead[], now: Date = new Date()): LeadTimelineModel {
  const todayMs = localMidnight(now);
  const points: LeadTimelinePoint[] = [];
  const undated: Lead[] = [];
  for (const lead of leads) {
    const date = parseLeadMoveInDate(lead.move_in_date);
    if (!date) {
      undated.push(lead);
      continue;
    }
    const tier = leadIntelTier(lead);
    const budget = leadBudgetNumber(lead);
    const t = date.getTime();
    points.push({
      id: lead.id,
      name: (lead.name || "").trim() || "Unknown",
      t,
      lane: laneIndex(tier),
      tier,
      tone: tierTone(tier),
      budget,
      z: budgetZ(budget),
      textsOk: leadTextsOk(lead),
      converted: lead.status === "converted",
      moveInLabel: formatTimelineDay(t),
      lead,
    });
  }
  points.sort((a, b) => a.t - b.t);
  const pastCount = points.filter((p) => p.t < todayMs).length;
  const next30Count = points.filter((p) => p.t >= todayMs && p.t <= todayMs + 30 * DAY_MS).length;
  const minT = points.length ? Math.min(points[0].t, todayMs - 7 * DAY_MS) : todayMs - 7 * DAY_MS;
  const maxT = points.length ? Math.max(points[points.length - 1].t, todayMs + 60 * DAY_MS) : todayMs + 60 * DAY_MS;
  // Pad the ends so the first/last dot never sits on the axis line.
  const domain: [number, number] = [minT - 3 * DAY_MS, maxT + 3 * DAY_MS];
  return { points, undated, pastCount, next30Count, domain, todayMs };
}

/**
 * The dashboard's LeadDetailModal reads a DashboardLeadRow; the Leads tab
 * holds Lead rows straight from the table. Same columns, two type names —
 * this maps them explicitly so a renamed field cannot silently go blank.
 */
export function leadToDashboardRow(lead: Lead): DashboardLeadRow {
  return {
    id: lead.id,
    created_at: lead.created_at,
    source: lead.source ?? null,
    name: lead.name ?? null,
    email: lead.email ?? null,
    phone: lead.phone ?? null,
    status: lead.status ?? null,
    budget: lead.budget ?? null,
    budget_number: typeof lead.budget_number === "number" ? lead.budget_number : null,
    neighborhoods: lead.neighborhoods ?? null,
    neighborhood_other: lead.neighborhood_other ?? null,
    pets: lead.pets ?? null,
    move_in_date: lead.move_in_date ?? null,
    verification_state: lead.verification_state ?? null,
    verification_score: typeof lead.verification_score === "number" ? lead.verification_score : null,
    verification_label: lead.verification_label ?? null,
    verification_provider: lead.verification_provider ?? null,
    verification_checked_at: lead.verification_checked_at ?? null,
    verification_flags: (lead.verification_flags as Record<string, unknown> | undefined) ?? null,
    email_list_opt_in: (lead as { email_list_opt_in?: boolean | null }).email_list_opt_in ?? null,
  };
}
